import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

// LiDAR point interface to match the main component
interface LidarPoint {
  height: number; // Original AMSL height
  intensity: number;
  lat: number;
  lng: number;
  metresFromPoint: number;
  heightAGL: number; // Height Above Ground Level (AGL)
  heightAMSL: number; // Original AMSL height for reference
  distanceFromPoint: number;
  x?: number;
  y?: number;
}

interface LidarVisualizationProps {
  position: { lat: number; lng: number };
  radius?: number;
  lidarData?: LidarPoint[];
  onDataLoad?: (data: LidarPoint[]) => void;
  onColumnWidthChange?: (width: number) => void;
  columnWidth?: number;
}

// Simplified OrbitControls implementation
class OrbitControls {
  object: THREE.Camera;
  domElement: HTMLElement;
  enabled: boolean = true;
  target: THREE.Vector3 = new THREE.Vector3();
  minDistance: number = 0;
  maxDistance: number = Infinity;
  minPolarAngle: number = 0;
  maxPolarAngle: number = Math.PI;
  enableDamping: boolean = false;
  dampingFactor: number = 0.05;
  enableZoom: boolean = true;
  zoomSpeed: number = 1.0;
  enableRotate: boolean = true;
  rotateSpeed: number = 1.0;
  enablePan: boolean = true;
  panSpeed: number = 1.0;
  autoRotate: boolean = false;
  autoRotateSpeed: number = 2.0;

  private STATE = { NONE: -1, ROTATE: 0, DOLLY: 1, PAN: 2 };
  private state = this.STATE.NONE;
  private spherical = new THREE.Spherical();
  private sphericalDelta = new THREE.Spherical();
  private panOffset = new THREE.Vector3();
  private scale = 1;
  private rotateStart = new THREE.Vector2();
  private rotateEnd = new THREE.Vector2();
  private rotateDelta = new THREE.Vector2();
  private panStart = new THREE.Vector2();
  private panEnd = new THREE.Vector2();
  private panDelta = new THREE.Vector2();
  private dollyStart = new THREE.Vector2();
  private dollyEnd = new THREE.Vector2();
  private dollyDelta = new THREE.Vector2();

  constructor(object: THREE.Camera, domElement: HTMLElement) {
    this.object = object;
    this.domElement = domElement;
    this.setupEventListeners();
    this.update();
  }

  private setupEventListeners() {
    this.domElement.addEventListener('mousedown', this.onMouseDown.bind(this), false);
    this.domElement.addEventListener('wheel', this.onMouseWheel.bind(this), false);
    this.domElement.addEventListener('contextmenu', (e) => e.preventDefault(), false);
  }

  private getZoomScale() {
    return Math.pow(0.95, this.zoomSpeed);
  }

  private rotateLeft(angle: number) {
    this.sphericalDelta.theta -= angle;
  }

  private rotateUp(angle: number) {
    this.sphericalDelta.phi -= angle;
  }

  private onMouseDown(event: MouseEvent) {
    if (!this.enabled) return;
    event.preventDefault();

    switch (event.button) {
      case 0:
        this.state = this.STATE.ROTATE;
        this.rotateStart.set(event.clientX, event.clientY);
        break;
      case 1:
        this.state = this.STATE.DOLLY;
        this.dollyStart.set(event.clientX, event.clientY);
        break;
      case 2:
        this.state = this.STATE.PAN;
        this.panStart.set(event.clientX, event.clientY);
        break;
    }

    if (this.state !== this.STATE.NONE) {
      document.addEventListener('mousemove', this.onMouseMove.bind(this), false);
      document.addEventListener('mouseup', this.onMouseUp.bind(this), false);
    }
  }

  private onMouseMove(event: MouseEvent) {
    if (!this.enabled) return;
    event.preventDefault();

    switch (this.state) {
      case this.STATE.ROTATE:
        this.rotateEnd.set(event.clientX, event.clientY);
        this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.rotateSpeed);
        this.rotateLeft(2 * Math.PI * this.rotateDelta.x / this.domElement.clientHeight);
        this.rotateUp(2 * Math.PI * this.rotateDelta.y / this.domElement.clientHeight);
        this.rotateStart.copy(this.rotateEnd);
        this.update();
        break;
      case this.STATE.PAN:
        this.panEnd.set(event.clientX, event.clientY);
        this.panDelta.subVectors(this.panEnd, this.panStart).multiplyScalar(this.panSpeed);
        this.pan(this.panDelta.x, this.panDelta.y);
        this.panStart.copy(this.panEnd);
        this.update();
        break;
    }
  }

  private onMouseUp() {
    if (!this.enabled) return;
    document.removeEventListener('mousemove', this.onMouseMove.bind(this), false);
    document.removeEventListener('mouseup', this.onMouseUp.bind(this), false);
    this.state = this.STATE.NONE;
  }

  private onMouseWheel(event: WheelEvent) {
    if (!this.enabled || !this.enableZoom || this.state !== this.STATE.NONE) return;
    event.preventDefault();
    event.stopPropagation();

    if (event.deltaY < 0) {
      this.scale *= this.getZoomScale();
    } else if (event.deltaY > 0) {
      this.scale /= this.getZoomScale();
    }

    this.update();
  }

  private pan(deltaX: number, deltaY: number) {
    const offset = new THREE.Vector3();
    const position = this.object.position;
    offset.copy(position).sub(this.target);
    let targetDistance = offset.length();
    
    if (this.object instanceof THREE.PerspectiveCamera) {
      targetDistance *= Math.tan((this.object.fov / 2) * Math.PI / 180.0);
      this.panLeft(2 * deltaX * targetDistance / this.domElement.clientHeight);
      this.panUp(2 * deltaY * targetDistance / this.domElement.clientHeight);
    }
  }

  private panLeft(distance: number) {
    const v = new THREE.Vector3();
    v.setFromMatrixColumn(this.object.matrix, 0);
    v.multiplyScalar(-distance);
    this.panOffset.add(v);
  }

  private panUp(distance: number) {
    const v = new THREE.Vector3();
    v.setFromMatrixColumn(this.object.matrix, 1);
    v.multiplyScalar(distance);
    this.panOffset.add(v);
  }

  update() {
    const position = this.object.position;
    const offset = new THREE.Vector3();
    
    offset.copy(position).sub(this.target);
    this.spherical.setFromVector3(offset);

    if (this.autoRotate && this.state === this.STATE.NONE) {
      this.rotateLeft(2 * Math.PI / 60 / 60 * this.autoRotateSpeed * 0.1);
    }

    this.spherical.theta += this.sphericalDelta.theta;
    this.spherical.phi += this.sphericalDelta.phi;
    this.spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.spherical.phi));
    this.spherical.makeSafe();
    this.spherical.radius *= this.scale;
    this.spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, this.spherical.radius));

    this.target.add(this.panOffset);
    offset.setFromSpherical(this.spherical);
    position.copy(this.target).add(offset);
    this.object.lookAt(this.target);

    if (this.enableDamping) {
      this.sphericalDelta.theta *= (1 - this.dampingFactor);
      this.sphericalDelta.phi *= (1 - this.dampingFactor);
      this.panOffset.multiplyScalar(1 - this.dampingFactor);
    } else {
      this.sphericalDelta.set(0, 0, 0);
      this.panOffset.set(0, 0, 0);
    }

    this.scale = 1;
  }

  reset() {
    this.target.set(0, 0, 0);
    this.object.position.set(0, 20, 50);
    this.state = this.STATE.NONE;
    this.update();
  }

  dispose() {
    this.domElement.removeEventListener('mousedown', this.onMouseDown.bind(this));
    this.domElement.removeEventListener('wheel', this.onMouseWheel.bind(this));
    this.domElement.removeEventListener('contextmenu', (e) => e.preventDefault());
  }
}

const LidarVisualization3D: React.FC<LidarVisualizationProps> = ({
  position,
  radius = 3,
  lidarData = [],
  onDataLoad,
  onColumnWidthChange,
  columnWidth: propColumnWidth = 4
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const controlsRef = useRef<OrbitControls>();
  const frameRef = useRef<number>();
  const pointCloudRef = useRef<THREE.Points>();
  const heightColumnsRef = useRef<THREE.Object3D[]>([]);
  
  const [stats, setStats] = useState({
    totalPoints: 0,
    maxHeight: 0,
    avgHeight: 0,
    obstacleCount: 0,
    heightRange: '0 - 0',
    distanceRange: '0 - 0'
  });
  
  // Fixed controls for production use
  const colorBy = 'height';
  const pointSize = 0.8;
  const heightScale = 1.3;
  const density = 100;
  const columnWidth = propColumnWidth;
  const [autoRotate, setAutoRotate] = useState(true);

  // Color gradient function
  const getColorFromGradient = (value: number): THREE.Color => {
    value = Math.max(0, Math.min(1, value));
    const color = new THREE.Color();
    
    if (value < 0.25) {
      color.setRGB(0, value * 4, 1);
    } else if (value < 0.5) {
      color.setRGB(0, 1, 1 - (value - 0.25) * 4);
    } else if (value < 0.75) {
      color.setRGB((value - 0.5) * 4, 1, 0);
    } else {
      color.setRGB(1, 1 - (value - 0.75) * 4, 0);
    }
    
    return color;
  };

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.Fog(0x000000, 50, 200);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 20, 50);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    mountRef.current.appendChild(renderer.domElement);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 5;
    controls.maxDistance = 200;
    controls.maxPolarAngle = Math.PI * 0.9;
    controlsRef.current = controls;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight.position.set(10, 50, 20);
    scene.add(directionalLight);

    // Grid helper
    const gridHelper = new THREE.GridHelper(100, 100, 0x444444, 0x222222);
    scene.add(gridHelper);

    // Axes helper
    const axesHelper = new THREE.AxesHelper(20);
    scene.add(axesHelper);

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current) return;
      
      const newWidth = mountRef.current.clientWidth;
      const newHeight = mountRef.current.clientHeight;
      
      cameraRef.current.aspect = newWidth / newHeight;
      cameraRef.current.updateProjectionMatrix();
      
      rendererRef.current.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    // Animation loop
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      if (controlsRef.current) {
        controlsRef.current.dispose();
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Update stats when lidarData changes
  useEffect(() => {
    if (onDataLoad) {
      onDataLoad(lidarData);
    }
    
    if (lidarData.length > 0) {
      const totalPoints = lidarData.length;
      const heights = lidarData.map(p => p.heightAGL); // Now using AGL heights
      const distances = lidarData.map(p => p.distanceFromPoint);
      const maxHeight = Math.max(...heights);
      const minHeight = Math.min(...heights);
      const avgHeight = heights.reduce((sum, h) => sum + h, 0) / totalPoints;
      
      // Count obstacles based on AGL height thresholds
      const lowObstacles = lidarData.filter(p => p.heightAGL > 0.5 && p.heightAGL <= 2).length;
      const mediumObstacles = lidarData.filter(p => p.heightAGL > 2 && p.heightAGL <= 10).length;
      const highObstacles = lidarData.filter(p => p.heightAGL > 10).length;
      const obstacleCount = lowObstacles + mediumObstacles + highObstacles;
      
      setStats({
        totalPoints,
        maxHeight,
        avgHeight,
        obstacleCount,
        heightRange: `${minHeight.toFixed(1)} - ${maxHeight.toFixed(1)}m AGL`,
        distanceRange: `${Math.min(...distances).toFixed(3)} - ${Math.max(...distances).toFixed(3)}`
      });
    } else {
      setStats({
        totalPoints: 0,
        maxHeight: 0,
        avgHeight: 0,
        obstacleCount: 0,
        heightRange: '0 - 0',
        distanceRange: '0 - 0'
      });
    }
  }, [lidarData, onDataLoad]);

  // Add height reference columns
  const addHeightColumns = () => {
    if (!sceneRef.current) return;

    // Remove existing columns
    heightColumnsRef.current.forEach(column => {
      sceneRef.current!.remove(column);
    });
    heightColumnsRef.current = [];

    // Calculate scaling for fixed column radii
    const maxHorizontalRange = lidarData.length > 0 ? Math.max(
      Math.max(...lidarData.map(p => p.x || 0)) - Math.min(...lidarData.map(p => p.x || 0)),
      Math.max(...lidarData.map(p => p.y || 0)) - Math.min(...lidarData.map(p => p.y || 0)),
      10 // Minimum scale to ensure visibility
    ) : 10;
    const horizontalScale = 20 / maxHorizontalRange;
    
    // Fixed column radii in meters, converted to scene scale
    const greenColumnRadius = 2.1 * horizontalScale; // 2.1m radius for green column
    const redColumnRadius = 3.1 * horizontalScale;   // 3.1m radius for red column

    // Column 1: 0m to 14m (green) - 2.1m radius
    const geometry1 = new THREE.CylinderGeometry(greenColumnRadius, greenColumnRadius, 14 * heightScale, 32, 1, true);
    const material1 = new THREE.MeshPhongMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      wireframe: false,
      shininess: 30
    });
    const column1 = new THREE.Mesh(geometry1, material1);
    column1.position.set(0, (14 * heightScale) / 2, 0);
    sceneRef.current.add(column1);
    heightColumnsRef.current.push(column1);

    // Add wireframe outline
    const wireframe1 = new THREE.WireframeGeometry(geometry1);
    const wireframeMaterial1 = new THREE.LineBasicMaterial({ color: 0x00aa00, linewidth: 2 });
    const wireframeMesh1 = new THREE.LineSegments(wireframe1, wireframeMaterial1);
    wireframeMesh1.position.set(0, (14 * heightScale) / 2, 0);
    sceneRef.current.add(wireframeMesh1);
    heightColumnsRef.current.push(wireframeMesh1);

    // Column 2: 14m to 45m (red) - 3.1m radius
    const geometry2 = new THREE.CylinderGeometry(redColumnRadius, redColumnRadius, 31 * heightScale, 32, 1, true);
    const material2 = new THREE.MeshPhongMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      wireframe: false,
      shininess: 30
    });
    const column2 = new THREE.Mesh(geometry2, material2);
    column2.position.set(0, (14 + 31/2) * heightScale, 0);
    sceneRef.current.add(column2);
    heightColumnsRef.current.push(column2);

    // Add wireframe outline
    const wireframe2 = new THREE.WireframeGeometry(geometry2);
    const wireframeMaterial2 = new THREE.LineBasicMaterial({ color: 0xaa0000, linewidth: 2 });
    const wireframeMesh2 = new THREE.LineSegments(wireframe2, wireframeMaterial2);
    wireframeMesh2.position.set(0, (14 + 31/2) * heightScale, 0);
    sceneRef.current.add(wireframeMesh2);
    heightColumnsRef.current.push(wireframeMesh2);

    // Add height labels
    const addLabel = (text: string, height: number, color: number, radius: number) => {
      const canvas = document.createElement('canvas');
      canvas.width = 320;
      canvas.height = 80;
      const context = canvas.getContext('2d')!;
      
      context.fillStyle = 'rgba(0, 0, 0, 0.9)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = '#ffffff';
      context.lineWidth = 3;
      context.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
      
      context.font = 'Bold 42px Arial';
      context.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, canvas.width / 2, canvas.height / 2);
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.position.set(radius + 4, height, 0);
      sprite.scale.set(4, 1.5, 1);
      sceneRef.current!.add(sprite);
      heightColumnsRef.current.push(sprite);
    };

    addLabel('0m', 2, 0x00ff00, greenColumnRadius);
    addLabel('14m', 14 * heightScale, 0x00ff00, greenColumnRadius);
    addLabel('45m', 45 * heightScale, 0xff0000, redColumnRadius);
  };

  // Update visualization
  const updateVisualization = () => {
    if (!sceneRef.current || lidarData.length === 0) return;

    // Remove existing point cloud
    if (pointCloudRef.current) {
      sceneRef.current.remove(pointCloudRef.current);
      pointCloudRef.current.geometry.dispose();
      (pointCloudRef.current.material as THREE.Material).dispose();
    }

    // Calculate ranges for normalization
    const heights = lidarData.map(p => p.heightAGL);
    const minHeight = Math.min(...heights);
    const maxHeight = Math.max(...heights);

    // Calculate horizontal ranges for proper scaling
    const xValues = lidarData.map(p => p.x || 0);
    const yValues = lidarData.map(p => p.y || 0);
    const maxHorizontalRange = Math.max(
      Math.max(...xValues) - Math.min(...xValues),
      Math.max(...yValues) - Math.min(...yValues),
      10 // Minimum scale to ensure visibility
    );
    
    // Use consistent scale that maintains proper proportions
    // Scale to make the horizontal spread fit nicely in the scene (around 20 units)
    const horizontalScale = 20 / maxHorizontalRange;

    // Create geometry
    const geometry = new THREE.BufferGeometry();
    const positions: number[] = [];
    const colors: number[] = [];

    // Sample points based on density
    const step = Math.max(1, Math.floor(100 / density));
    
    for (let i = 0; i < lidarData.length; i += step) {
      const point = lidarData[i];
      if (!point.x || !point.y) continue;
      
      // Position with accurate scaling
      const x = point.x * horizontalScale; // Accurate horizontal scaling
      const y = (point.heightAGL - minHeight) * heightScale;
      const z = point.y * horizontalScale; // Accurate horizontal scaling
      positions.push(x, y, z);

      // Color based on height
      const value = (point.heightAGL - minHeight) / (maxHeight - minHeight);
      const color = getColorFromGradient(value);
      colors.push(color.r, color.g, color.b);
    }

    // Set attributes
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeBoundingSphere();

    // Create material
    const material = new THREE.PointsMaterial({
      size: pointSize,
      vertexColors: true,
      sizeAttenuation: true,
      fog: true
    });

    // Create point cloud
    const pointCloud = new THREE.Points(geometry, material);
    sceneRef.current.add(pointCloud);
    pointCloudRef.current = pointCloud;
  };

  // Update controls
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  // Notify parent of column width
  useEffect(() => {
    if (onColumnWidthChange) {
      onColumnWidthChange(columnWidth);
    }
  }, [onColumnWidthChange, columnWidth]);

  // Update visualization when controls change
  useEffect(() => {
    updateVisualization();
  }, [lidarData, colorBy, pointSize, heightScale, density]);

  // Update height columns when scale changes
  useEffect(() => {
    addHeightColumns();
  }, [heightScale, columnWidth, lidarData]);

  const resetView = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  return (
    <div className="bg-black border border-gray-700 rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-4 border-b border-gray-700 bg-gray-900">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-white">Real-time LiDAR Analysis</h3>
            <p className="text-xs text-blue-400 mt-1">Analysis radius: 10m (LiDAR) • Heights shown as Above Ground Level (AGL) • Accurate proportional scaling</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setAutoRotate(!autoRotate)}
              className={`px-3 py-1 text-sm rounded ${
                autoRotate 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {autoRotate ? 'Stop Rotation' : 'Auto Rotate'}
            </button>
            <button
              onClick={resetView}
              className="px-3 py-1 text-sm rounded bg-gray-700 text-gray-300 hover:bg-gray-600"
            >
              Reset View
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-1">
          Points: {stats.totalPoints} | Height Range: {stats.heightRange} | Distance: {stats.distanceRange}m | Horizontal Spread: {lidarData.length > 0 ? Math.max(
            Math.max(...lidarData.map(p => p.x || 0)) - Math.min(...lidarData.map(p => p.x || 0)),
            Math.max(...lidarData.map(p => p.y || 0)) - Math.min(...lidarData.map(p => p.y || 0))
          ).toFixed(1) : '0'}m
        </p>
      </div>

      {/* 3D Visualization */}
      <div className="p-4 bg-black">
        <div className="relative">
          <div
            ref={mountRef}
            className="border border-gray-700 rounded bg-black cursor-grab active:cursor-grabbing w-full h-full"
            style={{ minHeight: 400 }}
          />
          
          {/* Interactive controls info */}
          <div className="absolute top-2 left-2 bg-black bg-opacity-80 text-green-400 p-2 rounded text-xs border border-gray-700">
            <div>🖱️ Left drag: Rotate</div>
            <div>🖱️ Right drag: Pan</div>
            <div>🎯 Scroll: Zoom</div>
          </div>

          {/* Legend */}
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white p-2 rounded text-xs border border-gray-700">
            <div className="font-semibold mb-1">Height Legend</div>
            <div className="flex items-center gap-1 mb-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>0-14m Reference</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>14-45m Reference</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="p-4 border-t border-gray-700 bg-gray-900">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-400">Max Height AGL</div>
            <div className="text-white font-semibold">{stats.maxHeight.toFixed(1)}m</div>
          </div>
          <div>
            <div className="text-gray-400">Avg Height AGL</div>
            <div className="text-white font-semibold">{stats.avgHeight.toFixed(1)}m</div>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="text-xs text-green-400">
            <strong>AGL Heights:</strong> All heights converted to Above Ground Level for accurate obstacle assessment • Real LiDAR point cloud data from geo API
          </div>
        </div>
      </div>
    </div>
  );
};

export default LidarVisualization3D;